import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { Card } from '../components/ui';
import type { Role } from '../types';

interface Kachel {
  to: string;
  titel: string;
  text: string;
  roles: Role[];
}

/**
 * GF/Admin-Cockpit: ein Einstieg für Verwaltung und Konfiguration, statt vieler
 * Einzelpunkte im Menü. Jede Kachel ist rollengefiltert und verlinkt auf die
 * bestehende Unterseite.
 */
const GRUPPEN: { titel: string; kacheln: Kachel[] }[] = [
  {
    titel: 'Benutzer & Rechte',
    kacheln: [
      { to: '/benutzer', titel: 'Benutzer', text: 'Zugänge anlegen, Rollen zuweisen, Passwörter zurücksetzen.', roles: ['admin'] },
      { to: '/rollen', titel: 'Rollen & Rechte', text: 'Je Rolle Bereiche und einzelne Menüpunkte freigeben.', roles: ['admin'] },
    ],
  },
  {
    titel: 'Konfiguration',
    kacheln: [
      { to: '/fonds', titel: 'Fonds (Abteilungen)', text: 'Abteilungen je Auftraggeber für die Abrechnung pflegen.', roles: ['admin', 'kaufmann', 'disponent'] },
      { to: '/module', titel: 'Module & Betriebsart', text: 'Betriebsart, buchbare Module und Lizenz verwalten.', roles: ['admin'] },
      { to: '/bridges', titel: 'Offline-Bridge', text: 'Standort-Bridges registrieren und Status prüfen.', roles: ['admin'] },
    ],
  },
  {
    titel: 'Betrieb & Nachweis',
    kacheln: [
      { to: '/system', titel: 'System & Datensicherung', text: 'Zeitpläne, Jobs und Stand der Datensicherung.', roles: ['admin', 'kaufmann'] },
      { to: '/protokoll', titel: 'Protokoll', text: 'Änderungs- und Zugriffsprotokoll des Systems.', roles: ['admin', 'kaufmann'] },
    ],
  },
];

export function CockpitPage() {
  const { can, verborgen, menuVersteckt } = useAuth();
  const sichtbarRoute = (to: string) => !verborgen.has(to) && !menuVersteckt.has(to);

  return (
    <>
      <Card>
        <h3 style={{ marginTop: 0 }}>Verwaltung & Konfiguration</h3>
        <p className="small muted" style={{ marginBottom: 0 }}>
          Zentraler Einstieg für Geschäftsführung und Administration. Es werden nur die Bereiche
          angezeigt, für die du berechtigt bist.
        </p>
      </Card>

      {GRUPPEN.map((gruppe) => {
        const sichtbar = gruppe.kacheln.filter((k) => can(...k.roles) && sichtbarRoute(k.to));
        if (sichtbar.length === 0) return null;
        return (
          <Card key={gruppe.titel} title={gruppe.titel} padded={false}>
            <div
              className="body"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}
            >
              {sichtbar.map((k) => (
                <Link
                  key={k.to}
                  to={k.to}
                  className="kachel"
                  style={{
                    display: 'block',
                    padding: 14,
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{k.titel}</div>
                  <div className="small muted">{k.text}</div>
                </Link>
              ))}
            </div>
          </Card>
        );
      })}
    </>
  );
}
